import { useCallback, useEffect, useState, useRef } from 'react'
import { API } from '../../api'
import { TADStreamAndApp } from '../../type'
import { handleArrayBufferBackendError } from '../../utility/array-buffer-toast-handling'

export const LAST_MEMBERSHIPS_TAB_ACCESSED = 'lastMembershipsTabAccessed'

type TUseStreamsAndApps = {
  isLoading: boolean
  streamsAndApps: TADStreamAndApp[]
  search: string
  setSearch: (search: string) => void
  page: number
  setPage: (page: number) => void
  totalCount: number
  maxPageSize: number
  totalPages: number
  getStreamsAndApps: () => void
  setStreamsAndApps: (streamAndApp: TADStreamAndApp[]) => void
  handleExportStreamsAndAppsButtonClick: () => void
}

export const useStreamsAndApps = (streamsOnly: boolean): TUseStreamsAndApps => {
  const [isLoading, setIsLoading] = useState(true)
  const [streams, setStreams] = useState<TADStreamAndApp[]>([])
  const [apps, setApps] = useState<TADStreamAndApp[]>([])
  const [search, setSearch] = useState('')
  const [streamsPage, setStreamsPage] = useState(1)
  const [appsPage, setAppsPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [maxPageSize, setMaxPageSize] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const abortControllerRef = useRef<AbortController | null>(null)
  const requestIdRef = useRef(0)

  const getMemberships = useCallback(
    async (pageToFetch: number) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      setIsLoading(true)
      requestIdRef.current += 1
      const currentRequestId = requestIdRef.current
      abortControllerRef.current = new AbortController()

      setIsLoading(true)

      try {
        const { max_page_size, total_pages, total_count, page_number, results } = await API.ADManagement.streamsAndApplications.list(
          search,
          pageToFetch,
          streamsOnly,
          abortControllerRef.current.signal,
        )

        setMaxPageSize(Number(max_page_size))
        setTotalCount(Number(total_count))
        setTotalPages(Number(total_pages))
        if (streamsOnly) {
          setStreams(results)
          setStreamsPage(Number(page_number))
        } else {
          setApps(results)
          setAppsPage(Number(page_number))
        }
      } catch (error) {
        if ((error as { name?: string }).name === 'AbortError') {
          console.log('Request was aborted')
        } else {
          console.error('Request failed', error)
        }
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false)
        }
      }
    },
    [search, streamsOnly],
  )

  useEffect(() => {
    setIsLoading(true)
    getMemberships(streamsOnly ? streamsPage : appsPage)

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [getMemberships, streamsPage, appsPage, streamsOnly])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (streamsOnly) {
        setStreamsPage(newPage)
      } else {
        setAppsPage(newPage)
      }
    },
    [streamsOnly],
  )

  const handleExportStreamsAndAppsButtonClick = () => {
    try {
      void API.ADManagement.streamsAndApplications.download(streamsOnly, search)
    } catch (error) {
      handleArrayBufferBackendError(error)
    }
  }

  return {
    isLoading,
    streamsAndApps: streamsOnly ? streams : apps,
    search,
    setSearch,
    page: streamsOnly ? streamsPage : appsPage,
    setPage: handlePageChange,
    totalCount,
    maxPageSize,
    totalPages,
    getStreamsAndApps: () => getMemberships(streamsOnly ? streamsPage : appsPage),
    setStreamsAndApps: streamsOnly ? setStreams : setApps,
    handleExportStreamsAndAppsButtonClick,
  }
}
